<template>
  <div class="card">
    <h2 class="card__title">{{ t("distribution.title") }}</h2>
    <p class="card__sub">{{ t("distribution.lede") }}</p>

    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)"><div class="notice__body">{{ error }}</div></div>
    <div v-if="loading" class="row"><span class="spinner" aria-hidden="true"></span><span class="muted small">{{ t("common.loading") }}</span></div>

    <template v-else>
      <div class="stack distribution__modes">
        <label class="checkbox">
          <input v-model="mode" type="radio" value="all" />
          <span><span class="strong">{{ t("distribution.all") }}</span><br /><span class="muted small">{{ t("distribution.allHint") }}</span></span>
        </label>
        <label class="checkbox">
          <input v-model="mode" type="radio" value="allowlist" />
          <span><span class="strong">{{ t("distribution.allowlist") }}</span><br /><span class="muted small">{{ t("distribution.allowlistHint") }}</span></span>
        </label>
      </div>

      <div v-if="mode === 'allowlist'" class="distribution__picker">
        <div class="row row--between row--wrap">
          <input v-model.trim="filter" class="input distribution__filter" type="search" :placeholder="t('distribution.filter')" :aria-label="t('distribution.filter')" />
          <span class="small muted">{{ t("distribution.selected", { count: selected.length, total: installations.length }) }}</span>
        </div>
        <p v-if="!installations.length" class="muted small">{{ t("distribution.noInstallations") }}</p>
        <ul v-else class="distribution__list">
          <li v-for="inst in visible" :key="inst.id">
            <label class="checkbox">
              <input v-model="selected" type="checkbox" :value="inst.id" />
              <span>
                <span class="strong">{{ inst.name }}</span> <code class="xs">{{ inst.code }}</code>
                <br /><span class="muted xs">{{ inst.base_url }}</span>
              </span>
            </label>
          </li>
        </ul>
      </div>

      <p class="small muted">{{ t("distribution.partnerNote") }}</p>

      <div class="row">
        <button class="btn" :disabled="busy || (mode === 'allowlist' && !selected.length)" @click="save">
          <span v-if="busy" class="spinner" aria-hidden="true"></span>
          {{ busy ? t("common.saving") : t("common.save") }}
        </button>
        <span v-if="saved" class="badge badge--success">{{ t("common.saved") }}</span>
        <span v-if="mode === 'allowlist' && !selected.length" class="small muted">{{ t("distribution.pickOne") }}</span>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../../lib/api";
import { useErrorText } from "../../lib/errors";
import { loadInstallations } from "../../lib/installations";

const props = defineProps({ appId: { type: String, required: true } });
const { t } = useI18n();
const errorText = useErrorText();

const mode = ref("all");
const selected = ref([]);
const installations = ref([]);
const filter = ref("");
const loading = ref(true);
const busy = ref(false);
const saved = ref(false);
const error = ref("");

const visible = computed(() => {
  const q = filter.value.toLowerCase();
  if (!q) return installations.value;
  return installations.value.filter((i) =>
    [i.name, i.code, i.base_url].some((v) => String(v || "").toLowerCase().includes(q))
  );
});

onMounted(async () => {
  try {
    const [dist, list] = await Promise.all([
      api.distribution(props.appId).catch((e) => (e.status === 404 ? null : Promise.reject(e))),
      loadInstallations()
    ]);
    installations.value = list;
    mode.value = dist?.mode === "allowlist" ? "allowlist" : "all";
    selected.value = Array.isArray(dist?.installation_ids) ? [...dist.installation_ids] : [];
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
});

async function save() {
  busy.value = true;
  error.value = "";
  saved.value = false;
  try {
    await api.saveDistribution(props.appId, {
      mode: mode.value,
      installation_ids: mode.value === "allowlist" ? selected.value : []
    });
    saved.value = true;
    setTimeout(() => (saved.value = false), 2500);
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
</script>

<style scoped>
.distribution__modes { margin: var(--space-4) 0; }
.distribution__picker { border: 1px solid var(--line); border-radius: var(--radius); padding: var(--space-4); margin-bottom: var(--space-4); }
.distribution__filter { max-width: 20rem; }
.distribution__list { list-style: none; margin: var(--space-3) 0 0; padding: 0; max-height: 22rem; overflow-y: auto; }
.distribution__list li { padding: var(--space-2) 0; border-bottom: 1px solid var(--line); }
.distribution__list li:last-child { border-bottom: 0; }
</style>
